import React, { useEffect, useRef, useState } from "react";
import {
  List,
  Box,
  Paper,
  useMediaQuery,
  useTheme,
  Typography,
  Divider,
} from "@mui/material";
import { Message } from "../models/Message";
import ChatBubble from "./ChatBubble";
import { useConnection } from "../contexts/ConnectionContext";
import { useAppDispatch, useAppSelector } from "../hooks/hooks";
import { resetUnreadCount } from "../redux/chatListSlice";

// Get width from environment variable with fallback
const MOBILE_APP_WIDTH = parseInt(
  process.env.REACT_APP_MOBILE_APP_WIDTH || "500"
);

export interface ChatWindowProps {
  //messages: Message[];
}

export const ChatWindow: React.FC<ChatWindowProps> = ({}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${MOBILE_APP_WIDTH}px)`);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const { messages } = useAppSelector((state) => state.messages);
  const prevMessagesLength = useRef(messages.length);
  const dispatch = useAppDispatch();

  const { connection } = useConnection();
  const { user } = useAppSelector((state) => state.user);

  // Handle scroll events to detect user position
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        scrollContainerRef.current;
      const threshold = 50; // pixels from bottom
      setIsAtBottom(scrollHeight - (scrollTop + clientHeight) < threshold);
    }
  };

  // Scroll to bottom only if new messages arrive and we're at bottom
  useEffect(() => {
    if (
      messagesEndRef.current &&
      (isAtBottom || messages.length !== prevMessagesLength.current)
    ) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
    prevMessagesLength.current = messages.length;
  }, [messages, isAtBottom]);

  useEffect(() => {
    // Initial scroll
    messagesEndRef.current?.scrollIntoView();

    // Set up scroll listener
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      return () => scrollContainer.removeEventListener("scroll", handleScroll);
    }
  }, []);

  useEffect(() => {
    if (!connection || !user) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const unreadMessages = entries
          .filter((entry) => entry.isIntersecting)
          .map((entry) => {
            const messageId = entry.target.getAttribute("data-message-id");
            return messages.find((msg) => msg.id === messageId);
          })
          .filter(
            (msg) =>
              msg &&
              msg.direction === "incoming" &&
              msg.status !== "read" &&
              msg.sender !== user.walletAddress
          );

        if (unreadMessages.length > 0) {
          const messageIds = unreadMessages.map((msg) => msg!.id);
          connection
            .invoke("MarkMessagesAsRead", messageIds)
            .catch((err: unknown) =>
              console.error("Error sending read receipt:", err)
            );
          dispatch(resetUnreadCount({ contactId: user.walletAddress }));
        }
      },
      { threshold: 0.5 } // 50% of message visible
    );

    // Observe all messages
    const messageElements = document.querySelectorAll("[data-message-id]");
    messageElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [messages, connection, user]);

  const scrollableBoxStyles = {
    height: isMobile ? "calc(100dvh - 370px)" : "48vh",
    overflowY: "auto",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
    WebkitOverflowScrolling: "touch",
  };

  // Group messages by date
  const groupMessagesByDate = (messages: Message[]) => {
    const grouped: { [date: string]: Message[] } = {};

    // Create a new array before sorting
    const sortedMessages = [...messages].sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );

    sortedMessages.forEach((msg) => {
      const date = new Date(msg.timestamp).toLocaleDateString(undefined, {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(msg);
    });

    return grouped;
  };

  const groupedMessages = React.useMemo(
    () => groupMessagesByDate(messages),
    [messages]
  );

  return (
    <Paper
      sx={{
        p: isMobile ? 1 : 2,
        width: "100%",
        height: "100%",
        maxWidth: MOBILE_APP_WIDTH,
        margin: "auto",
        ...(isMobile && {
          maxWidth: "100%",
          borderRadius: 0,
          boxShadow: "none",
        }),
        ...(!isMobile && {
          borderRadius: 0,
          boxShadow: 0,
        }),
      }}
    >
      <Box ref={scrollContainerRef} sx={scrollableBoxStyles}>
        <List
          sx={{
            height: "100%",
            overflow: "auto",
            p: isMobile ? 1 : 2,
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            WebkitOverflowScrolling: "touch",
          }}
        >
          {Object.entries(groupedMessages).map(([date, messages]) => (
            <React.Fragment key={date}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  my: 2,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    px: 2,
                    py: 0.5,
                    borderRadius: 4,
                    backgroundColor: theme.palette.grey[800],
                    color: theme.palette.text.secondary,
                  }}
                >
                  {date}
                </Typography>
              </Box>
              {messages.map((msg) => (
                <ChatBubble key={msg.id} message={msg} />
              ))}
            </React.Fragment>
          ))}
          <div ref={messagesEndRef} />
        </List>
      </Box>
    </Paper>
  );
};
